import { sanitizeSourceHtmlForEmission } from './html';

export const LEGACY_TABLE_RUNTIME_ATTRIBUTE =
  'data-wysiwyg-editor-legacy-table';
export const LEGACY_TABLE_SELECTOR = 'table:not(.content-table)';

const ALLOWED_ELEMENTS = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CAPTION',
  'CODE',
  'COL',
  'COLGROUP',
  'DIV',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'HR',
  'I',
  'IMG',
  'LI',
  'OL',
  'P',
  'PRE',
  'S',
  'SPAN',
  'STRIKE',
  'STRONG',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
  'U',
  'UL',
]);

const REMOVE_WITH_CONTENT = new Set([
  'AUDIO',
  'CANVAS',
  'EMBED',
  'IFRAME',
  'INPUT',
  'OBJECT',
  'SCRIPT',
  'SOURCE',
  'STYLE',
  'VIDEO',
]);

// Class values remain selector hooks for TED and hotel stylesheets, but the
// opaque node view removes them from the Admin visual DOM. The style policy
// below constrains inline declarations independently; a class may still supply
// positioning context for an allowed top or z-index value.
const GLOBAL_ATTRIBUTES = new Set([
  'class',
  'dir',
  'id',
  'lang',
  'role',
  'style',
  'title',
]);
const ATTRIBUTES_BY_ELEMENT = new Map<string, ReadonlySet<string>>([
  [
    'A',
    new Set([
      'data-app',
      'data-folder',
      'data-item',
      'data-page',
      'href',
      'name',
      'rel',
      'target',
      'title',
    ]),
  ],
  ['COL', new Set(['align', 'char', 'charoff', 'span', 'valign', 'width'])],
  [
    'COLGROUP',
    new Set(['align', 'char', 'charoff', 'span', 'valign', 'width']),
  ],
  ['IMG', new Set(['alt', 'height', 'src', 'title', 'width'])],
  [
    'TABLE',
    new Set([
      'align',
      'bgcolor',
      'border',
      'cellpadding',
      'cellspacing',
      'frame',
      'height',
      'rules',
      'summary',
      'width',
    ]),
  ],
  ['TBODY', new Set(['align', 'char', 'charoff', 'valign'])],
  [
    'TD',
    new Set([
      'abbr',
      'align',
      'axis',
      'bgcolor',
      'char',
      'charoff',
      'colspan',
      'headers',
      'height',
      'nowrap',
      'rowspan',
      'scope',
      'valign',
      'width',
    ]),
  ],
  ['TFOOT', new Set(['align', 'char', 'charoff', 'valign'])],
  [
    'TH',
    new Set([
      'abbr',
      'align',
      'axis',
      'bgcolor',
      'char',
      'charoff',
      'colspan',
      'headers',
      'height',
      'nowrap',
      'rowspan',
      'scope',
      'valign',
      'width',
    ]),
  ],
  ['THEAD', new Set(['align', 'char', 'charoff', 'valign'])],
  ['TR', new Set(['align', 'bgcolor', 'char', 'charoff', 'height', 'valign'])],
]);

const ALLOWED_STYLE_PROPERTIES = new Set([
  'background',
  'background-color',
  'border',
  'border-bottom',
  'border-bottom-color',
  'border-bottom-style',
  'border-bottom-width',
  'border-collapse',
  'border-color',
  'border-left',
  'border-left-color',
  'border-left-style',
  'border-left-width',
  'border-right',
  'border-right-color',
  'border-right-style',
  'border-right-width',
  'border-spacing',
  'border-style',
  'border-top',
  'border-top-color',
  'border-top-style',
  'border-top-width',
  'border-width',
  'clear',
  'color',
  'display',
  'float',
  'font-style',
  'font-weight',
  'height',
  'line-height',
  'margin',
  'margin-bottom',
  'margin-left',
  'margin-right',
  'margin-top',
  'max-height',
  'max-width',
  'min-height',
  'min-width',
  'overflow',
  'overflow-x',
  'overflow-y',
  'padding',
  'padding-bottom',
  'padding-left',
  'padding-right',
  'padding-top',
  'table-layout',
  'text-align',
  'text-decoration',
  'text-transform',
  'top',
  'vertical-align',
  'white-space',
  'width',
  'z-index',
]);

function isAllowedStyleValue(
  element: HTMLElement,
  property: string,
  value: string
) {
  if (/(?:expression|javascript|url)\s*\(/i.test(value)) {
    return false;
  }

  if (property === 'top') {
    const match = /^(\d+(?:\.\d+)?)%$/.exec(value);
    const percentage = Number(match?.[1]);

    return (
      element.tagName === 'IMG' &&
      Boolean(match) &&
      percentage >= 0 &&
      percentage <= 100
    );
  }

  if (property === 'z-index') {
    const zIndex = Number(value);

    return (
      element.tagName === 'TABLE' &&
      /^\d+$/.test(value) &&
      zIndex >= 0 &&
      zIndex <= 1000
    );
  }

  return true;
}

function removeUnsupportedElements(table: HTMLTableElement) {
  Array.from(table.querySelectorAll('*'))
    .reverse()
    .forEach((element) => {
      if (ALLOWED_ELEMENTS.has(element.tagName)) {
        return;
      }

      if (REMOVE_WITH_CONTENT.has(element.tagName)) {
        element.remove();
      } else {
        element.replaceWith(...element.childNodes);
      }
    });
}

function sanitizeStyle(element: HTMLElement) {
  const hasInsetDeclaration = [
    'inset',
    'inset-block',
    'inset-block-end',
    'inset-block-start',
    'inset-inline',
    'inset-inline-end',
    'inset-inline-start',
  ].some((property) => element.style.getPropertyValue(property));
  const declarations = Array.from(element.style)
    .filter(
      (property) =>
        ALLOWED_STYLE_PROPERTIES.has(property) &&
        (!hasInsetDeclaration || property !== 'top')
    )
    .flatMap((property) => {
      const value = element.style.getPropertyValue(property).trim();

      if (
        !value ||
        element.style.getPropertyPriority(property) ||
        !isAllowedStyleValue(element, property, value)
      ) {
        return [];
      }

      return [`${property}: ${value};`];
    });

  if (declarations.length) {
    element.setAttribute('style', declarations.join(' '));
  } else {
    element.removeAttribute('style');
  }
}

function sanitizeAttributes(table: HTMLTableElement) {
  [table, ...table.querySelectorAll('*')].forEach((element) => {
    const elementAttributes = ATTRIBUTES_BY_ELEMENT.get(element.tagName);

    Array.from(element.attributes).forEach((attribute) => {
      if (
        !GLOBAL_ATTRIBUTES.has(attribute.name) &&
        !/^aria-[a-z0-9_.:-]+$/i.test(attribute.name) &&
        !elementAttributes?.has(attribute.name)
      ) {
        element.removeAttribute(attribute.name);
      }
    });

    if (element instanceof HTMLElement && element.hasAttribute('style')) {
      sanitizeStyle(element);
    }
  });
}

export function createSanitizedLegacyTableElement(
  value: unknown
): HTMLTableElement | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const document = new DOMParser().parseFromString(
    sanitizeSourceHtmlForEmission(value),
    'text/html'
  );
  const [element] = document.body.children;

  if (
    document.body.children.length !== 1 ||
    !(element instanceof HTMLTableElement) ||
    element.classList.contains('content-table')
  ) {
    return null;
  }

  removeUnsupportedElements(element);
  sanitizeAttributes(element);

  return element;
}
